import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { Rating } from '@mui/material';
import {
  propertyApi,
  type PropertyData,
  PropertyQualityLabels,
  type EvaluationBreakdown,
  type MarketDataEntry,
  type CalculationStep,
  formatGewType,
  formatCurrency,
  formatNumber,
  formatPercent,
} from '../services';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import PropertyEvaluationChart from '../components/PropertyEvaluationChart';
import PropertyFiles from '../components/PropertyFiles';
import { DatePicker } from '../components/DatePicker';
import './PropertyPage.css';
import TrashIcon from '../assets/trash.svg';
import GraphIcon from '../assets/graph.svg';
import EuroIcon from '../assets/euro.svg';

const DevConditionClassificationScore: React.FC<{
  gewType?: string;
  features: any;
  ratings: any;
  targetType?: 'building' | 'land';
}> = ({ gewType, features, ratings, targetType = 'building' }) => {
  const [config, setConfig] = useState<any>(null);

  useEffect(() => {
    if (gewType) {
      propertyApi
        .getConditionClassificationConfig(gewType)
        .then(setConfig)
        .catch(console.error);
    }
  }, [gewType]);

  if (!config) return null;

  const calculateConditionClassificationDynamic = (
    features: any,
    ratings: any,
    config: any,
  ) => {
    let actualScore = 0;
    let maximumScore = 0;

    const getRawValue = (val: any) => {
      if (val && typeof val === 'object' && 'value' in val) {
        return val.value;
      }
      return val;
    };

    if (features && config.weights?.features) {
      for (const item of config.weights.features) {
        const itemTarget = item.target || 'building';
        if (itemTarget === targetType) {
          const featureObj = features[item.id];
          if (featureObj !== undefined && featureObj !== null) {
            const featureValue = getRawValue(featureObj);
            const val = featureValue ? 1 : 0;
            actualScore += val * item.value;
            maximumScore += 1 * item.value;
          }
        }
      }
    }

    if (ratings && config.weights?.ratings) {
      for (const item of config.weights.ratings) {
        const itemTarget = item.target || 'building';
        if (itemTarget === targetType) {
          const ratingObj = ratings[item.id];
          if (ratingObj !== undefined && ratingObj !== null) {
            const ratingValue = getRawValue(ratingObj);
            const val = Number(ratingValue);
            if (!isNaN(val)) {
              actualScore += (val - 1) * item.value;
              maximumScore += 2 * item.value;
            }
          }
        }
      }
    }

    if (ratings && config.weights?.numericRatings) {
      for (const item of config.weights.numericRatings) {
        const itemTarget = item.target || 'building';
        if (itemTarget === targetType) {
          const ratingObj = ratings[item.id];
          if (ratingObj !== undefined && ratingObj !== null) {
            const ratingValue = getRawValue(ratingObj);
            const val = Number(ratingValue);
            if (!isNaN(val)) {
              const refVal =
                item.maxValue !== undefined && item.maxValue > 0
                  ? item.maxValue
                  : item.referenceValue !== undefined && item.referenceValue > 0
                    ? item.referenceValue
                    : 1.0;
              actualScore += val * item.value;
              maximumScore += refVal * item.value;
            }
          }
        }
      }
    }
    if (features && config.weights?.enumFeatures) {
      for (const item of config.weights.enumFeatures) {
        const itemTarget = item.target || 'building';
        if (itemTarget === targetType) {
          const featureObj = features[item.id];
          if (featureObj !== undefined && featureObj !== null) {
            const featureValue = getRawValue(featureObj);
            const selectedOption = item.options?.find((o: any) => o.id === featureValue);
            if (selectedOption) {
              actualScore += selectedOption.value;
            }
            if (item.options && item.options.length > 0) {
              const maxVal = Math.max(...item.options.map((o: any) => o.value));
              maximumScore += maxVal;
            }
          }
        }
      }
    }
    const percentageScore =
      maximumScore > 0 ? (actualScore / maximumScore) * 100 : 0;

    let category: 'mäßig' | 'gut' | 'sehr gut';
    if (percentageScore < config.thresholds.low) {
      category = 'mäßig';
    } else if (percentageScore < config.thresholds.high) {
      category = 'gut';
    } else {
      category = 'sehr gut';
    }

    let scoreMin = 0;
    let scoreMax = 100;
    if (category === 'mäßig') {
      scoreMin = 0;
      scoreMax = config.thresholds.low;
    } else if (category === 'gut') {
      scoreMin = config.thresholds.low;
      scoreMax = config.thresholds.high;
    } else if (category === 'sehr gut') {
      scoreMin = config.thresholds.high;
      scoreMax = 100;
    }

    let fraction = 0;
    if (scoreMax > scoreMin) {
      fraction = (percentageScore - scoreMin) / (scoreMax - scoreMin);
    }
    fraction = Math.max(0, Math.min(1, fraction));

    return {
      actualScore,
      maximumScore,
      percentageScore,
      thresholds: config.thresholds,
      category,
      scoreMin,
      scoreMax,
      fraction,
    };
  };

  const getScoreDetails = () => {
    if (!config) return [];
    const details: any[] = [];

    const getRawValue = (val: any) => {
      if (val && typeof val === 'object' && 'value' in val) {
        return val.value;
      }
      return val;
    };

    if (features && config.weights?.features) {
      for (const item of config.weights.features) {
        const itemTarget = item.target || 'building';
        if (itemTarget === targetType) {
          const featureObj = features[item.id];
          if (featureObj !== undefined && featureObj !== null) {
            const featureValue = getRawValue(featureObj);
            const val = featureValue ? 1 : 0;
            details.push({
              name: item.localization || item.id,
              type: 'Ausstattung',
              value: featureValue ? 'Ja' : 'Nein',
              maxValue: 'Ja',
              points: val * item.value,
              maxPoints: 1 * item.value,
              weight: item.value,
            });
          }
        }
      }
    }

    if (ratings && config.weights?.ratings) {
      for (const item of config.weights.ratings) {
        const itemTarget = item.target || 'building';
        if (itemTarget === targetType) {
          const ratingObj = ratings[item.id];
          if (ratingObj !== undefined && ratingObj !== null) {
            const ratingValue = getRawValue(ratingObj);
            const val = Number(ratingValue);
            if (!isNaN(val)) {
              details.push({
                name: item.localization || item.id,
                type: 'Bewertung',
                value: `${val} ★`,
                maxValue: '3 ★',
                points: (val - 1) * item.value,
                maxPoints: 2 * item.value,
                weight: item.value,
              });
            }
          }
        }
      }
    }

    if (ratings && config.weights?.numericRatings) {
      for (const item of config.weights.numericRatings) {
        const itemTarget = item.target || 'building';
        if (itemTarget === targetType) {
          const ratingObj = ratings[item.id];
          if (ratingObj !== undefined && ratingObj !== null) {
            const ratingValue = getRawValue(ratingObj);
            const val = Number(ratingValue);
            if (!isNaN(val)) {
              const refVal =
                item.maxValue !== undefined && item.maxValue > 0
                  ? item.maxValue
                  : item.referenceValue !== undefined && item.referenceValue > 0
                    ? item.referenceValue
                    : 1.0;
              details.push({
                name: item.localization || item.id,
                type: 'Numerisch',
                value: `${val}`,
                maxValue: `${refVal}`,
                points: val * item.value,
                maxPoints: refVal * item.value,
                weight: item.value,
              });
            }
          }
        }
      }
    }
    if (features && config.weights?.enumFeatures) {
      for (const item of config.weights.enumFeatures) {
        const itemTarget = item.target || 'building';
        if (itemTarget === targetType) {
          const featureObj = features[item.id];
          if (featureObj !== undefined && featureObj !== null) {
            const featureValue = getRawValue(featureObj);
            const selectedOption = item.options?.find((o: any) => o.id === featureValue);
            const maxVal = item.options && item.options.length > 0 ? Math.max(...item.options.map((o: any) => o.value)) : 0;
            const maxOption = item.options?.find((o: any) => o.value === maxVal);
            if (selectedOption) {
              details.push({
                name: item.localization || item.id,
                type: 'Auswahl',
                value: selectedOption.localization || selectedOption.id,
                maxValue: maxOption ? (maxOption.localization || maxOption.id) : '',
                points: selectedOption.value,
                maxPoints: maxVal,
                weight: 1,
              });
            }
          }
        }
      }
    }
    return details;
  };

  const {
    actualScore,
    maximumScore,
    percentageScore,
    thresholds,
    category,
    scoreMin,
    scoreMax,
    fraction,
  } = calculateConditionClassificationDynamic(features, ratings, config);

  const scoreDetails = getScoreDetails();
  const totalPoints = scoreDetails.reduce((sum, d) => sum + d.points, 0);
  const maxPointsTotal = scoreDetails.reduce((sum, d) => sum + d.maxPoints, 0);
  const percentTotal =
    maxPointsTotal > 0 ? (totalPoints / maxPointsTotal) * 100 : 0;

  return (
    <div
      style={{
        padding: '24px',
        border: '1px solid rgba(62, 207, 142, 0.3)',
        borderRadius: '20px',
        backgroundColor: 'rgba(10, 25, 20, 0.6)',
        backdropFilter: 'blur(10px)',
        fontFamily: 'monospace',
        color: '#fff',
        marginTop: '24px',
      }}
    >
      <h3
        style={{
          marginTop: 0,
          fontSize: '15px',
          color: '#3ecf8e',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          borderBottom: '1px solid rgba(62, 207, 142, 0.2)',
          paddingBottom: '10px',
          marginBottom: '20px',
          fontFamily: 'sans-serif',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#3ecf8e',
          }}
        ></span>
        Admin: Zustandsklassifizierungs-Score (
        {targetType === 'building' ? 'Gebäude/Wohnung' : 'Grundstück'})
      </h3>
      <div
        style={{
          marginBottom: '15px',
          fontSize: '13px',
          fontFamily: 'sans-serif',
          lineHeight: '1.6',
        }}
      >
        <div>
          <strong>Score:</strong> {actualScore} / {maximumScore} (
          {percentageScore.toFixed(2)}%)
        </div>
        <div>
          <strong>Klasse:</strong>{' '}
          <span
            style={{
              color:
                category === 'sehr gut'
                  ? '#4caf50'
                  : category === 'gut'
                    ? '#ffeb3b'
                    : '#f44336',
              fontWeight: 'bold',
              textTransform: 'uppercase',
            }}
          >
            {category}
          </span>
        </div>
        <div>
          <strong>Schwellenwerte:</strong> Mäßig &lt; {thresholds.low}% | Gut
          &lt; {thresholds.high}% | Sehr Gut &ge; {thresholds.high}%
        </div>
      </div>

      <div
        style={{
          position: 'relative',
          width: '100%',
          height: '8px',
          backgroundColor: 'rgba(255,255,255,0.1)',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            width: `${thresholds.low}%`,
            height: '100%',
            backgroundColor: '#f44336',
            opacity: 0.6,
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            left: `${thresholds.low}%`,
            width: `${thresholds.high - thresholds.low}%`,
            height: '100%',
            backgroundColor: '#ffeb3b',
            opacity: 0.6,
          }}
        ></div>
        <div
          style={{
            position: 'absolute',
            left: `${thresholds.high}%`,
            width: `${100 - thresholds.high}%`,
            height: '100%',
            backgroundColor: '#4caf50',
            opacity: 0.6,
          }}
        ></div>

        {/* Indicator */}
        <div
          style={{
            position: 'absolute',
            left: `calc(${Math.min(Math.max(percentageScore, 0), 100)}% - 6px)`,
            top: '-2px',
            width: '12px',
            height: '12px',
            backgroundColor: '#fff',
            borderRadius: '50%',
            boxShadow: '0 0 5px rgba(0,0,0,0.5)',
            zIndex: 2,
          }}
        ></div>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '11px',
          marginTop: '8px',
          color: '#aaa',
          fontFamily: 'sans-serif',
        }}
      >
        <span style={{ width: `${thresholds.low}%`, textAlign: 'center' }}>
          mäßig
        </span>
        <span
          style={{
            width: `${thresholds.high - thresholds.low}%`,
            textAlign: 'center',
          }}
        >
          gut
        </span>
        <span
          style={{ width: `${100 - thresholds.high}%`, textAlign: 'center' }}
        >
          sehr gut
        </span>
      </div>

      {/* Points Breakdown */}
      <details
        style={{
          marginTop: '16px',
          cursor: 'pointer',
          fontFamily: 'sans-serif',
        }}
      >
        <summary
          style={{ color: '#3ecf8e', fontSize: '13px', fontWeight: 600 }}
        >
          Punkte-Aufschlüsselung anzeigen
        </summary>
        <div style={{ marginTop: '8px', overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '11px',
              color: '#ccc',
            }}
          >
            <thead>
              <tr
                style={{
                  borderBottom: '1px solid rgba(255,255,255,0.15)',
                  textAlign: 'left',
                }}
              >
                <th style={{ padding: '6px 4px' }}>Merkmal</th>
                <th style={{ padding: '6px 4px' }}>Typ</th>
                <th style={{ padding: '6px 4px' }}>Wert</th>
                <th style={{ padding: '6px 4px' }}>Max. Wert</th>
                <th style={{ padding: '6px 4px' }}>Gewicht</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>
                  Punkte
                </th>
              </tr>
            </thead>
            <tbody>
              {scoreDetails.map((d, index) => (
                <tr
                  key={index}
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                >
                  <td style={{ padding: '6px 4px' }}>{d.name}</td>
                  <td style={{ padding: '6px 4px', opacity: 0.7 }}>{d.type}</td>
                  <td style={{ padding: '6px 4px' }}>{d.value}</td>
                  <td style={{ padding: '6px 4px', opacity: 0.7 }}>
                    {d.maxValue}
                  </td>
                  <td style={{ padding: '6px 4px' }}>{d.weight}</td>
                  <td
                    style={{
                      padding: '6px 4px',
                      textAlign: 'right',
                      fontFamily: 'monospace',
                    }}
                  >
                    {d.points.toFixed(1)} / {d.maxPoints.toFixed(1)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr
                style={{
                  borderTop: '2px solid rgba(255,255,255,0.15)',
                  fontWeight: 'bold',
                }}
              >
                <td style={{ padding: '6px 4px' }} colSpan={3}>
                  Gesamt
                </td>
                <td style={{ padding: '6px 4px' }}></td>
                <td style={{ padding: '6px 4px' }}></td>
                <td
                  style={{
                    padding: '6px 4px',
                    textAlign: 'right',
                    fontFamily: 'monospace',
                    color: '#3ecf8e',
                  }}
                >
                  {totalPoints.toFixed(1)} / {maxPointsTotal.toFixed(1)} ={' '}
                  {percentTotal.toFixed(2)}%
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </details>

      {/* Formula/Fraction Box */}
      <div
        style={{
          marginTop: '16px',
          padding: '10px',
          backgroundColor: 'rgba(255,255,255,0.03)',
          borderRadius: '8px',
          fontFamily: 'monospace',
          fontSize: '12px',
        }}
      >
        <strong>Klassen-Prozentwert (Fraction):</strong>
        <br />
        Formula: (Score - Min) / (Max - Min)
        <br />
        Calculation: ({percentageScore.toFixed(2)} - {scoreMin}) / ({scoreMax} -{' '}
        {scoreMin}) ={' '}
        <strong style={{ color: '#3ecf8e' }}>
          {(fraction * 100).toFixed(2)}%
        </strong>
      </div>
    </div>
  );
};

const AdminEvaluationBreakdown: React.FC<{
  propertyId: string;
  propertyGewType?: string;
}> = ({ propertyId, propertyGewType }) => {
  const [breakdown, setBreakdown] = useState<EvaluationBreakdown | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    propertyApi
      .getEvaluationBreakdown(propertyId)
      .then((data) => {
        setBreakdown(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Fehler beim Laden der Bewertungsdetails');
        setLoading(false);
      });
  }, [propertyId]);

  if (loading) {
    return (
      <div
        style={{
          padding: '24px',
          border: '1px solid rgba(62, 207, 142, 0.3)',
          borderRadius: '20px',
          backgroundColor: 'rgba(10, 25, 20, 0.6)',
          backdropFilter: 'blur(10px)',
          color: '#fff',
          marginTop: '24px',
          fontFamily: 'sans-serif',
        }}
      >
        <h3
          style={{
            marginTop: 0,
            fontSize: '15px',
            color: '#3ecf8e',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            borderBottom: '1px solid rgba(62, 207, 142, 0.2)',
            paddingBottom: '10px',
            marginBottom: '20px',
          }}
        >
          Admin: Bewertungs-Detailanalyse
        </h3>
        <div style={{ color: '#aaa' }}>Lade Analyse...</div>
      </div>
    );
  }

  if (error || !breakdown) {
    return (
      <div
        style={{
          padding: '24px',
          border: '1px solid rgba(244, 67, 54, 0.3)',
          borderRadius: '20px',
          backgroundColor: 'rgba(25, 10, 10, 0.6)',
          backdropFilter: 'blur(10px)',
          color: '#fff',
          marginTop: '24px',
          fontFamily: 'sans-serif',
        }}
      >
        <h3
          style={{
            marginTop: 0,
            fontSize: '15px',
            color: '#f44336',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            borderBottom: '1px solid rgba(244, 67, 54, 0.2)',
            paddingBottom: '10px',
            marginBottom: '20px',
          }}
        >
          Admin: Bewertungs-Detailanalyse
        </h3>
        <div style={{ color: '#ff6b6b' }}>
          {error || 'Keine Daten verfügbar'}
        </div>
      </div>
    );
  }

  const {
    classification,
    buildingMarketData,
    landMarketData,
    buildingCalculation,
    landCalculation,
    mainCalculation,
    staticValueTotal,
    staticValueDetails,
    finalValue,
  } = breakdown;

  const renderMarketData = (
    title: string,
    data?: MarketDataEntry,
    calc?: CalculationStep,
    targetClass?: any,
  ) => {
    if (!data || !calc || !targetClass) return null;
    return (
      <div
        style={{
          marginBottom: '20px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          paddingBottom: '15px',
        }}
      >
        <h4
          style={{
            color: '#3ecf8e',
            margin: '0 0 10px 0',
            fontSize: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          {title}
        </h4>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '10px',
            fontSize: '13px',
            color: '#ccc',
          }}
        >
          <div>
            <strong>Gemeinde:</strong> {data.gewGemName}
          </div>
          <div>
            <strong>Datenquelle:</strong> {data.gewType}
          </div>
          <div>
            <strong>Min/Max (Klasse):</strong> {formatNumber(calc.valueLwr)} € -{' '}
            {formatNumber(calc.valueUpr)} € / m²
          </div>
          <div>
            <strong>Berechneter Wert/m²:</strong>{' '}
            {formatNumber(calc.valuePerSqm, { maximumFractionDigits: 2 })} € /
            m²
          </div>
          <div
            style={{
              gridColumn: 'span 2',
              marginTop: '6px',
              padding: '8px',
              backgroundColor: 'rgba(255,255,255,0.02)',
              borderRadius: '6px',
              fontFamily: 'monospace',
              fontSize: '11px',
            }}
          >
            {data.gewType === 'Bauland' ? (
              <>
                <strong>Rechenweg:</strong> Direkter Wert aus lokaler
                Marktdatenbank basierend auf Zustand ({targetClass.category}){' '}
                <br />
                Berechnung:{' '}
                <strong style={{ color: '#3ecf8e' }}>
                  {formatNumber(calc.valuePerSqm, { maximumFractionDigits: 2 })}{' '}
                  € / m²
                </strong>
              </>
            ) : (
              <>
                <strong>Rechenweg:</strong> Min + Fraction × (Max - Min) <br />
                Berechnung: {formatNumber(calc.valueLwr)} € +{' '}
                {formatNumber(targetClass.fraction, {
                  minimumFractionDigits: 4,
                  maximumFractionDigits: 4,
                })}{' '}
                × ({formatNumber(calc.valueUpr)} € -{' '}
                {formatNumber(calc.valueLwr)} €) ={' '}
                <strong style={{ color: '#3ecf8e' }}>
                  {formatNumber(calc.valuePerSqm, { maximumFractionDigits: 2 })}{' '}
                  € / m²
                </strong>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      style={{
        padding: '24px',
        border: '1px solid rgba(62, 207, 142, 0.3)',
        borderRadius: '20px',
        backgroundColor: 'rgba(10, 25, 20, 0.6)',
        backdropFilter: 'blur(10px)',
        color: '#fff',
        marginTop: '24px',
        fontFamily: 'sans-serif',
      }}
    >
      <h3
        style={{
          marginTop: 0,
          fontSize: '15px',
          color: '#3ecf8e',
          textTransform: 'uppercase',
          letterSpacing: '1px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          borderBottom: '1px solid rgba(62, 207, 142, 0.2)',
          paddingBottom: '10px',
          marginBottom: '20px',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#3ecf8e',
          }}
        ></span>
        Admin: Bewertungs-Detailanalyse
      </h3>

      {/* Step 1: Selected Local Market Data */}
      <div style={{ marginBottom: '24px' }}>
        <h4
          style={{
            color: '#3ecf8e',
            margin: '0 0 12px 0',
            fontSize: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          1. Marktdaten & Klassen-Eingrenzung
        </h4>
        {propertyGewType && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px',
              fontSize: '13px',
              color: '#ccc',
              marginBottom: '20px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
              paddingBottom: '15px',
            }}
          >
            <div>
              <strong>GewType (Immobilie):</strong>{' '}
              {propertyGewType
                .replace(/-/g, ' ')
                .replace(/\b\w/g, (l) => l.toUpperCase())}
            </div>
          </div>
        )}
        {buildingMarketData &&
          renderMarketData(
            buildingCalculation ? 'Gebäude-Daten' : 'Daten (Wohnung)',
            buildingMarketData,
            buildingCalculation || mainCalculation,
            classification.building,
          )}
        {landMarketData &&
          renderMarketData(
            landCalculation ? 'Grundstücks-Daten' : 'Daten (Grundstück)',
            landMarketData,
            landCalculation || mainCalculation,
            classification.land,
          )}
      </div>

      {/* Step 2: Final Math */}
      <div>
        <h4
          style={{
            color: '#3ecf8e',
            margin: '0 0 12px 0',
            fontSize: '14px',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          2. Endwert-Berechnung
        </h4>
        <div
          style={{
            padding: '15px',
            backgroundColor: 'rgba(62, 207, 142, 0.05)',
            border: '1px solid rgba(62, 207, 142, 0.1)',
            borderRadius: '10px',
            fontSize: '13px',
            lineHeight: '1.8',
          }}
        >
          {buildingCalculation && (
            <div>
              <strong>Gebäude-Wert:</strong>{' '}
              {formatNumber(buildingCalculation.valuePerSqm, {
                maximumFractionDigits: 2,
              })}
              € / m² × {formatNumber(buildingCalculation.area)} m² ={' '}
              <strong>
                {formatCurrency(buildingCalculation.total, { maxDecimals: 0 })}
              </strong>
            </div>
          )}
          {landCalculation && (
            <div>
              <strong>Grundstücks-Wert:</strong>{' '}
              {formatNumber(landCalculation.valuePerSqm, {
                maximumFractionDigits: 2,
              })}
              € / m² × {formatNumber(landCalculation.area)} m² ={' '}
              <strong>
                {formatCurrency(landCalculation.total, { maxDecimals: 0 })}
              </strong>
            </div>
          )}
          {mainCalculation && (
            <div>
              <strong>Basis-Wert:</strong>{' '}
              {formatNumber(mainCalculation.valuePerSqm, {
                maximumFractionDigits: 2,
              })}
              € / m² × {formatNumber(mainCalculation.area)} m² ={' '}
              <strong>
                {formatCurrency(mainCalculation.total, { maxDecimals: 0 })}
              </strong>
            </div>
          )}
          {staticValueTotal > 0 && (
            <div style={{ marginTop: '8px' }}>
              {staticValueDetails && staticValueDetails.length > 0 ? (
                <>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Zusatzwerte:</strong>
                  </div>
                  {staticValueDetails.map((detail, idx) => (
                    <div
                      key={idx}
                      style={{
                        paddingLeft: '12px',
                        color: '#bbb',
                        display: 'flex',
                        justifyContent: 'space-between',
                        maxWidth: '300px',
                      }}
                    >
                      <span>{detail.name}:</span>
                      <span>+ {formatCurrency(detail.value)}</span>
                    </div>
                  ))}
                  <div
                    style={{
                      paddingLeft: '12px',
                      marginTop: '4px',
                      borderTop: '1px solid rgba(255,255,255,0.1)',
                      paddingTop: '4px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      maxWidth: '300px',
                    }}
                  >
                    <strong>Summe Zusatzwerte:</strong>
                    <strong>+ {formatCurrency(staticValueTotal)}</strong>
                  </div>
                </>
              ) : (
                <div>
                  <strong>Zusatzwerte (Garagen, etc.):</strong> +{' '}
                  {formatCurrency(staticValueTotal)}
                </div>
              )}
            </div>
          )}
          <div
            style={{
              borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              marginTop: '10px',
              paddingTop: '10px',
              fontSize: '15px',
            }}
          >
            <strong>Errechneter Gesamtwert:</strong>{' '}
            <strong style={{ color: '#3ecf8e', fontSize: '18px' }}>
              {formatCurrency(finalValue, { maxDecimals: 0 })}
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
};

const TruncatedTooltipValue: React.FC<{ value: string }> = ({ value }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = () => {
    const el = ref.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    }
  };

  return (
    <div className="field-value" style={{ width: '100%', minWidth: 0 }}>
      <span
        ref={ref}
        onMouseEnter={checkTruncation}
        title={isTruncated ? value : undefined}
        style={{
          display: 'block',
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {value}
      </span>
    </div>
  );
};

const PropertyPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();

  type TimePeriod = '6months' | '12months' | '5years' | 'all';
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('all');

  const emptyProperty: PropertyData = {
    id: '',
    address: '',
    country: 'Österreich',
    gain: 0,
    gainPercentage: 0,
    features: {},
    ratings: {},
  };

  const [property, setProperty] = useState<PropertyData>(emptyProperty);
  const [classificationConfig, setClassificationConfig] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showSellWizard, setShowSellWizard] = useState<boolean>(false);
  const [sellAmount, setSellAmount] = useState<string>('');
  const [sellDate, setSellDate] = useState<string>('');
  const [showBuyModal, setShowBuyModal] = useState<boolean>(false);
  const [buyPrice, setBuyPrice] = useState<string>('');
  const [buyDate, setBuyDate] = useState<string>('');
  const evaluationRefreshTrigger = 0;
  const [isMapInitialized, setIsMapInitialized] = useState<boolean>(false);

  useEffect(() => {
    if (property.gewType) {
      propertyApi
        .getConditionClassificationConfig(property.gewType)
        .then(setClassificationConfig)
        .catch(console.error);
    }
  }, [property.gewType]);

  // Refs for map
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerInstanceRef = useRef<google.maps.Marker | null>(null);

  // Function to update map location
  const updateMapLocation = (lat: number, lng: number) => {
    if (mapInstanceRef.current) {
      const position = { lat, lng };
      mapInstanceRef.current.setCenter(position);
      mapInstanceRef.current.setZoom(15);

      const googleMaps = (window as any).google;
      if (markerInstanceRef.current) {
        markerInstanceRef.current.setPosition(position);
      } else if (googleMaps && googleMaps.maps) {
        markerInstanceRef.current = new googleMaps.maps.Marker({
          position,
          map: mapInstanceRef.current,
        });
      }
    }
  };

  // Function to initialize map (can be called multiple times safely)
  const initializeMap = () => {
    if (!mapRef.current) {
      return false;
    }

    // Check if element is actually in the DOM
    if (!document.contains(mapRef.current)) {
      return false;
    }

    // Ensure the container has dimensions - Google Maps requires explicit dimensions
    const rect = mapRef.current.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return false;
    }

    const googleMaps = (window as any).google;
    if (!googleMaps || !googleMaps.maps || !googleMaps.maps.Map) {
      return false;
    }

    if (!mapInstanceRef.current) {
      try {
        // Check if Google Maps already initialized this container
        const hasGoogleMapsContent =
          mapRef.current.querySelector('[class*="gm-"]') ||
          mapRef.current.querySelector('div[style*="overflow"]');

        if (!hasGoogleMapsContent) {
          // Clear only if no Google Maps content exists
          mapRef.current.innerHTML = '';
        }

        mapInstanceRef.current = new googleMaps.maps.Map(mapRef.current, {
          center: { lat: 47.5162, lng: 14.5501 }, // Default to Austria
          zoom: 7,
          disableDefaultUI: true,
        });

        // Trigger resize after a short delay to ensure map renders correctly
        setTimeout(() => {
          if (mapInstanceRef.current && mapRef.current) {
            const googleMaps = (window as any).google;
            if (googleMaps && googleMaps.maps && googleMaps.maps.event) {
              googleMaps.maps.event.trigger(mapInstanceRef.current, 'resize');

              // Re-initialize if container was cleared after resize
              if (mapRef.current.children.length === 0) {
                mapInstanceRef.current = null;
                setIsMapInitialized(false);
                initializeMap();
              }
            }
          }
        }, 200);

        setIsMapInitialized(true);
        return true;
      } catch {
        return false;
      }
    }

    return true;
  };

  // Initialize map - retry until Google Maps is loaded
  // Also re-initialize when property ID changes (navigate to different property)
  useEffect(() => {
    // Store the current ID to prevent race conditions
    const currentId = id;
    let isMounted = true;

    // Reset map instance and state when property ID changes
    // mapInstanceRef.current = null; // Don't reset map instance to avoid flickering/reloading if possible, but for ID change we might need to repointer
    // Actually for view page, ID won't change often without full navig logic
    // But let's keep original logic for safety
    mapInstanceRef.current = null;
    markerInstanceRef.current = null;
    setIsMapInitialized(false);

    // Small delay to ensure DOM is ready and avoid React StrictMode double-initialization issues
    const initTimeout = setTimeout(() => {
      if (!isMounted || currentId !== id) return;

      // Try to initialize immediately
      if (initializeMap()) {
        return; // Map initialized successfully
      }

      // If not initialized, set up a retry mechanism
      let retryCount = 0;
      const maxRetries = 100; // 10 seconds max wait time (100 * 100ms)

      const retryInterval = setInterval(() => {
        if (!isMounted || currentId !== id) {
          clearInterval(retryInterval);
          return;
        }

        retryCount++;
        if (initializeMap()) {
          clearInterval(retryInterval);
        } else if (retryCount >= maxRetries) {
          clearInterval(retryInterval);
        }
      }, 100);

      return () => clearInterval(retryInterval);
    }, 50);

    return () => {
      isMounted = false;
      clearTimeout(initTimeout);
      markerInstanceRef.current = null;
    };
  }, [id]); // Re-run when property ID changes

  // Update map location when coordinates are available (e.g., after save or when property loads)
  useEffect(() => {
    if (
      property.latitude !== undefined &&
      property.longitude !== undefined &&
      property.latitude !== null &&
      property.longitude !== null
    ) {
      // Wait for map to be initialized before updating
      if (isMapInitialized && mapInstanceRef.current) {
        // Map is ready, update location immediately
        updateMapLocation(property.latitude, property.longitude);
      } else {
        // Map not ready yet, wait a bit and try again
        let retryCount = 0;
        const maxRetries = 100;

        const tryUpdate = () => {
          if (isMapInitialized && mapInstanceRef.current) {
            updateMapLocation(property.latitude!, property.longitude!);
          } else if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(tryUpdate, 100);
          }
        };

        setTimeout(tryUpdate, 100);
      }
    }
  }, [property.latitude, property.longitude, isMapInitialized]);

  // Helper function to convert property from API format to display format
  const normalizeProperty = (prop: PropertyData): PropertyData => {
    const cleanProp = { ...prop } as any;
    delete cleanProp.userId;
    delete cleanProp.user;

    return {
      ...cleanProp,
      kaufdatum: cleanProp.kaufdatum
        ? typeof cleanProp.kaufdatum === 'string'
          ? cleanProp.kaufdatum.split('T')[0]
          : new Date(cleanProp.kaufdatum).toISOString().split('T')[0]
        : undefined,
      // Convert string numbers from backend to actual numbers
      kaufpreis:
        cleanProp.kaufpreis !== undefined && cleanProp.kaufpreis !== null
          ? typeof cleanProp.kaufpreis === 'string'
            ? parseFloat(cleanProp.kaufpreis)
            : cleanProp.kaufpreis
          : undefined,
      currentValue:
        cleanProp.currentValue !== undefined && cleanProp.currentValue !== null
          ? typeof cleanProp.currentValue === 'string'
            ? parseFloat(cleanProp.currentValue)
            : cleanProp.currentValue
          : undefined,
      gain:
        typeof cleanProp.gain === 'string'
          ? parseFloat(cleanProp.gain)
          : cleanProp.gain,
      gainPercentage:
        typeof cleanProp.gainPercentage === 'string'
          ? parseFloat(cleanProp.gainPercentage)
          : cleanProp.gainPercentage,
      // Convert latitude/longitude from string to number if needed
      latitude:
        cleanProp.latitude !== undefined && cleanProp.latitude !== null
          ? typeof cleanProp.latitude === 'string'
            ? parseFloat(cleanProp.latitude)
            : cleanProp.latitude
          : undefined,
      longitude:
        cleanProp.longitude !== undefined && cleanProp.longitude !== null
          ? typeof cleanProp.longitude === 'string'
            ? parseFloat(cleanProp.longitude)
            : cleanProp.longitude
          : undefined,
      sellDate: cleanProp.sellDate
        ? typeof cleanProp.sellDate === 'string'
          ? cleanProp.sellDate.split('T')[0]
          : new Date(cleanProp.sellDate).toISOString().split('T')[0]
        : undefined,
      sellAmount:
        cleanProp.sellAmount !== undefined && cleanProp.sellAmount !== null
          ? typeof cleanProp.sellAmount === 'string'
            ? parseFloat(cleanProp.sellAmount)
            : cleanProp.sellAmount
          : undefined,
      area:
        cleanProp.area !== undefined && cleanProp.area !== null
          ? typeof cleanProp.area === 'string'
            ? parseFloat(cleanProp.area)
            : cleanProp.area
          : undefined,
      plotArea:
        cleanProp.plotArea !== undefined && cleanProp.plotArea !== null
          ? typeof cleanProp.plotArea === 'string'
            ? parseFloat(cleanProp.plotArea)
            : cleanProp.plotArea
          : undefined,
      baujahr:
        cleanProp.baujahr !== undefined && cleanProp.baujahr !== null
          ? typeof cleanProp.baujahr === 'string'
            ? parseInt(cleanProp.baujahr, 10)
            : cleanProp.baujahr
          : undefined,
      eigenkapital:
        cleanProp.eigenkapital !== undefined && cleanProp.eigenkapital !== null
          ? typeof cleanProp.eigenkapital === 'string'
            ? parseFloat(cleanProp.eigenkapital)
            : cleanProp.eigenkapital
          : undefined,
      fremdkapital:
        cleanProp.fremdkapital !== undefined && cleanProp.fremdkapital !== null
          ? typeof cleanProp.fremdkapital === 'string'
            ? parseFloat(cleanProp.fremdkapital)
            : cleanProp.fremdkapital
          : undefined,
      zinssatz:
        cleanProp.zinssatz !== undefined && cleanProp.zinssatz !== null
          ? typeof cleanProp.zinssatz === 'string'
            ? parseFloat(cleanProp.zinssatz)
            : cleanProp.zinssatz
          : undefined,
      monatlicheRate:
        cleanProp.monatlicheRate !== undefined &&
        cleanProp.monatlicheRate !== null
          ? typeof cleanProp.monatlicheRate === 'string'
            ? parseFloat(cleanProp.monatlicheRate)
            : cleanProp.monatlicheRate
          : undefined,
      laufzeit:
        cleanProp.laufzeit !== undefined && cleanProp.laufzeit !== null
          ? typeof cleanProp.laufzeit === 'string'
            ? parseInt(cleanProp.laufzeit, 10)
            : cleanProp.laufzeit
          : undefined,
      // Always set country to Österreich if not set
      country: cleanProp.country || 'Österreich',
      features: cleanProp.features || {},
      ratings: cleanProp.ratings || {},
    };
  };

  useEffect(() => {
    const fetchProperty = async () => {
      if (id) {
        setLoading(true);
        try {
          const propertyData = await propertyApi.getById(id);
          const normalized = normalizeProperty(propertyData);
          setProperty(normalized);
        } catch (error: any) {
          showError(error.message || 'Failed to load property');
          navigate('/dashboard');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchProperty();
  }, [id]);

  const handleDeleteProperty = async () => {
    if (!id) return;
    try {
      await propertyApi.delete(id);
      showSuccess('Immobilie erfolgreich entfernt');
      navigate('/dashboard');
    } catch (error: any) {
      showError(error.message || 'Fehler beim Entfernen der Immobilie');
    }
  };

  const handleSellProperty = async () => {
    if (!id || !sellAmount || !sellDate) return;
    try {
      setLoading(true);
      const updated = await propertyApi.sell(id, {
        sellAmount: parseFloat(sellAmount),
        sellDate,
      });
      setProperty(normalizeProperty(updated));
      setShowSellWizard(false);
      showSuccess('Immobilie erfolgreich als verkauft markiert');
    } catch (error: any) {
      showError(error.message || 'Fehler beim Speichern der Verkaufsdaten');
    } finally {
      setLoading(false);
    }
  };

  const handleBuyProperty = async () => {
    if (!id || !buyPrice || !buyDate) return;
    try {
      setLoading(true);
      const updated = await propertyApi.buy(id, {
        kaufpreis: parseFloat(buyPrice),
        kaufdatum: buyDate,
      });
      setProperty(normalizeProperty(updated));
      setShowBuyModal(false);
      showSuccess('Immobilie erfolgreich als gekauft markiert');
    } catch (error: any) {
      showError(error.message || 'Fehler beim Speichern der Kaufdaten');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="property-page">
        <div className="property-container">
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            Immobilie wird geladen...
          </div>
        </div>
      </div>
    );
  }

  const parsedAddress = (() => {
    if (!property.address) {
      return {
        streetName: property.streetName || '',
        houseNumber: property.houseNumber || '',
        stock: '',
        plz: '',
        city: property.city || '',
        country: property.country || 'Österreich',
      };
    }

    const parts = property.address.split(',').map((s) => s.trim());
    let streetAndHouse = parts[0] || '';
    const plzAndCity = parts[1] || '';
    const country = parts[2] || property.country || 'Österreich';

    let stock = '';
    if (streetAndHouse.includes('/')) {
      const slashParts = streetAndHouse.split('/');
      streetAndHouse = slashParts[0].trim();
      stock = slashParts[1] ? slashParts[1].trim() : '';
    }

    let streetName = property.streetName || '';
    let houseNumber = property.houseNumber || '';
    if (!streetName && !houseNumber && streetAndHouse) {
      const words = streetAndHouse.split(' ');
      if (words.length > 1) {
        const last = words[words.length - 1];
        if (/^\d+[a-zA-Z-]*$/.test(last)) {
          houseNumber = last;
          streetName = words.slice(0, -1).join(' ');
        } else {
          streetName = streetAndHouse;
        }
      } else {
        streetName = streetAndHouse;
      }
    } else {
      streetName = streetName || streetAndHouse;
    }

    let plz = '';
    let city = property.city || '';
    if (plzAndCity) {
      const words = plzAndCity.split(' ');
      if (words.length > 0 && /^\d+$/.test(words[0])) {
        plz = words[0];
        city = city || words.slice(1).join(' ');
      } else {
        city = city || plzAndCity;
      }
    }

    return {
      streetName,
      houseNumber,
      stock,
      plz,
      city,
      country,
    };
  })();

  return (
    <div className="property-page">
      <div className="property-container">

        <div className="property-action-row">
          {!property.sellDate &&
            (!property.kaufpreis || !property.kaufdatum) && (
              <button
                className="action-button primary"
                onClick={() => {
                  setBuyPrice('');
                  setBuyDate(new Date().toISOString().split('T')[0]);
                  setShowBuyModal(true);
                }}
                style={{ marginRight: '12px' }}
              >
                <img src={EuroIcon} alt="" />
                Immobilie kaufen
              </button>
            )}
          {!property.sellDate && property.kaufpreis && property.kaufdatum && (
            <button
              className="action-button primary"
              onClick={() => {
                setSellAmount('');
                setSellDate(new Date().toISOString().split('T')[0]);
                setShowSellWizard(true);
              }}
              style={{ marginRight: '12px' }}
            >
              <img src={EuroIcon} alt="" />
              Immobilie verkaufen
            </button>
          )}
          <button
            className="action-button secondary"
            onClick={() => setShowDeleteModal(true)}
          >
            <img src={TrashIcon} alt="" />
            Aus Portfolio entfernen
          </button>
        </div>

        <div className="property-content">
          <h1 className="property-title">
            {[
              [property.streetName, property.houseNumber]
                .filter(Boolean)
                .join(' '),
              property.city,
              property.country,
            ]
              .filter(Boolean)
              .join(' • ') ||
              property.address ||
              'Immobiliendetails'}
            {property.sellDate && (
              <span
                className="sold-badge"
                style={{
                  marginLeft: '16px',
                  padding: '4px 12px',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: '#f13050',
                  borderRadius: '8px',
                  textTransform: 'uppercase',
                  verticalAlign: 'middle',
                  display: 'inline-block',
                }}
              >
                Verkauft
              </span>
            )}
          </h1>

          <div className="property-layout">
            {/* Left Column */}
            <div className="property-left-column">
              {/* Allgemeine Informationen Section */}
              <div className="detail-section">
                <h2>Allgemeine Informationen</h2>

                {/* Map embedded inside the card */}
                <div
                  style={{
                    width: '100%',
                    height: '370px',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    marginTop: '8px',
                  }}
                >
                  <div
                    ref={mapRef}
                    className="map-container"
                    style={{
                      width: '100%',
                      height: '100%',
                      position: 'relative',
                    }}
                  />
                </div>

                <div className="detail-list" style={{ marginTop: '16px' }}>
                  <div className="detail-item-row">
                    <div className="detail-item" style={{ flex: '2 1 0%' }}>
                      <label>Adresse</label>
                      <div className="field-value">
                        {parsedAddress.streetName || '-'}
                      </div>
                    </div>
                    <div className="detail-item" style={{ flex: '1 1 0%' }}>
                      <label>Hausnummer</label>
                      <div className="field-value">
                        {parsedAddress.houseNumber || '-'}
                      </div>
                    </div>
                    <div className="detail-item" style={{ flex: '1 1 0%' }}>
                      <label>Stock</label>
                      <div className="field-value">
                        {parsedAddress.stock || '-'}
                      </div>
                    </div>
                  </div>

                  <div className="detail-item-row">
                    <div className="detail-item" style={{ flex: '2 1 0%' }}>
                      <label>Land</label>
                      <div className="field-value">
                        {parsedAddress.country || '-'}
                      </div>
                    </div>
                    <div className="detail-item" style={{ flex: '1 1 0%' }}>
                      <label>PLZ</label>
                      <div className="field-value">
                        {parsedAddress.plz || '-'}
                      </div>
                    </div>
                    <div
                      className="detail-item"
                      style={{ flex: '1 1 0%', minWidth: 0 }}
                    >
                      <label>Ort</label>
                      <TruncatedTooltipValue
                        value={parsedAddress.city || '-'}
                      />
                    </div>
                  </div>

                  <div className="detail-item-row">
                    <div className="detail-item">
                      <label>Immobilientyp</label>
                      <div className="field-value">
                        {formatGewType(property.gewType)}
                      </div>
                    </div>
                    <div className="detail-item">
                      <label>Größe</label>
                      <div className="field-value">
                        {property.area != null && property.plotArea != null ? (
                          <>
                            Wohnfläche: {formatNumber(property.area)} m²
                            <br />
                            Grundstück: {formatNumber(property.plotArea)} m²
                          </>
                        ) : property.area != null ? (
                          `${formatNumber(property.area)} m²`
                        ) : property.plotArea != null ? (
                          `${formatNumber(property.plotArea)} m²`
                        ) : (
                          '-'
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="detail-item-row">
                    <div className="detail-item">
                      <label>Baujahr</label>
                      <div className="field-value">
                        {property.baujahr != null ? property.baujahr : '-'}
                      </div>
                    </div>
                    <div className="detail-item">
                      <label>Zustand der Immobilie</label>
                      <div className="field-value">
                        {property.quality
                          ? PropertyQualityLabels[
                              property.quality as keyof typeof PropertyQualityLabels
                            ]
                          : '-'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Zustand & Qualität Section */}
              <div className="detail-section">
                <h2>Zustand & Qualität</h2>
                <div className="detail-list">
                  <div
                    className="detail-item-row"
                    style={{
                      flexDirection: 'column',
                      gap: '1rem',
                      alignItems: 'stretch',
                      width: '100%',
                    }}
                  >
                    {Object.entries(property.ratings || {}).map(
                      ([key, ratingObj]) => {
                        const val =
                          ratingObj &&
                          typeof ratingObj === 'object' &&
                          'value' in ratingObj
                            ? ratingObj.value
                            : Number(ratingObj) || 1;
                        const label =
                          ratingObj &&
                          typeof ratingObj === 'object' &&
                          'localization' in ratingObj
                            ? ratingObj.localization
                            : key;
                        const isNumeric =
                          classificationConfig?.weights?.numericRatings?.some(
                            (r: any) => r.id === key,
                          );
                        return (
                          <div
                            key={key}
                            className="detail-item"
                            style={{
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <label style={{ margin: 0, fontSize: '14px' }}>
                              {label}
                            </label>
                            <div
                              style={{ display: 'flex', alignItems: 'center' }}
                            >
                              {isNumeric ? (
                                <span
                                  style={{
                                    fontSize: '15px',
                                    fontWeight: 600,
                                    color: '#faaf00',
                                    backgroundColor: 'rgba(250, 175, 0, 0.1)',
                                    padding: '4px 10px',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(250, 175, 0, 0.2)',
                                  }}
                                >
                                  {val}
                                </span>
                              ) : (
                                <Rating
                                  value={val}
                                  max={3}
                                  precision={0.5}
                                  readOnly
                                />
                              )}
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>

                  <div
                    className="detail-section-subtitle"
                    style={{ marginTop: '16px' }}
                  >
                    Ausstattung & Lage
                  </div>
                  <div className="attributes-grid">
                    {Object.entries(property.features || {}).map(
                      ([key, featObj]) => {
                        const isActive =
                          featObj &&
                          typeof featObj === 'object' &&
                          'value' in featObj
                            ? featObj.value === true
                            : featObj === true;
                        const label =
                          featObj &&
                          typeof featObj === 'object' &&
                          'localization' in featObj
                            ? featObj.localization
                            : key;
                        return (
                          <div key={key} className="attribute-item">
                            <div
                              className={`attribute-icon ${isActive ? 'active' : 'inactive'}`}
                            >
                              {isActive ? '✓' : '✗'}
                            </div>
                            <div className="attribute-label">{label}</div>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              </div>

              {/* Dev Condition Classification Score Component */}
              {user?.role === 'admin' && (
                <>
                  {property.gewType !== 'grundstueck' && (
                    <DevConditionClassificationScore
                      gewType={property.gewType}
                      features={property.features}
                      ratings={property.ratings}
                      targetType="building"
                    />
                  )}
                  {(property.gewType?.includes('haus') ||
                    property.gewType === 'grundstueck') && (
                    <DevConditionClassificationScore
                      gewType={property.gewType}
                      features={property.features}
                      ratings={property.ratings}
                      targetType="land"
                    />
                  )}
                </>
              )}

              {/* Admin Evaluation Breakdown Component */}
              {user?.role === 'admin' && property.id && (
                <AdminEvaluationBreakdown
                  propertyId={property.id}
                  propertyGewType={property.gewType}
                />
              )}
            </div>

            {/* Right Column */}
            <div className="property-right-column">
              {/* Graph Section */}
              <div className="graph-section">
                <div className="graph-header">
                  <h3>Gesamtwert & Wertentwicklung</h3>
                  <img src={GraphIcon} alt="" className="graph-header-icon" />
                </div>

                <div className="graph-values">
                  <div className="graph-value-item">
                    <label>Geschätzter Gesamtwert</label>
                    <div className="graph-value-large">
                      {formatCurrency(property.currentValue)}
                      {property.kaufpreis !== undefined &&
                        property.kaufpreis !== null &&
                        property.gainPercentage !== undefined && (
                          <span
                            className={`graph-percentage ${property.gainPercentage >= 0 ? 'positive' : 'negative'}`}
                          >
                            {property.gainPercentage >= 0 ? '▲' : '▼'}{' '}
                            {formatPercent(
                              Math.abs(property.gainPercentage),
                              1,
                            )}
                          </span>
                        )}
                    </div>
                  </div>
                  <div className="graph-value-item">
                    <label>Geschätzte Wertentwicklung</label>
                    {property.kaufpreis !== undefined &&
                    property.kaufpreis !== null ? (
                      <div
                        className={`graph-value-large ${property.gain >= 0 ? 'positive' : 'negative'}`}
                      >
                        {property.gain >= 0 ? '+' : ''}
                        {formatCurrency(property.gain)}
                      </div>
                    ) : (
                      <div
                        className="graph-value-large"
                        style={{ color: 'var(--Grey-2, #a7a3e2)' }}
                      >
                        -
                      </div>
                    )}
                  </div>
                </div>

                <div className="time-scope-toggles">
                  <button
                    className={`toggle-button ${timePeriod === '6months' ? 'active' : ''}`}
                    onClick={() => setTimePeriod('6months')}
                  >
                    6 Monate
                  </button>
                  <button
                    className={`toggle-button ${timePeriod === '12months' ? 'active' : ''}`}
                    onClick={() => setTimePeriod('12months')}
                  >
                    1 Jahr
                  </button>
                  <button
                    className={`toggle-button ${timePeriod === 'all' ? 'active' : ''}`}
                    onClick={() => setTimePeriod('all')}
                  >
                    Max.
                  </button>
                </div>

                <div className="graph-chart-container">
                  <PropertyEvaluationChart
                    property={property}
                    refreshTrigger={evaluationRefreshTrigger}
                    timePeriod={timePeriod}
                  />
                </div>

                {property.isFallbackEvaluation && (
                  <div
                    style={{
                      backgroundColor: '#e7f1ff',
                      color: '#0c5460',
                      padding: '12px',
                      borderRadius: '8px',
                      marginTop: '16px',
                      fontSize: '14px',
                      border: '1px solid #b8daff',
                      lineHeight: '1.4',
                    }}
                  >
                    <strong>Hinweis:</strong> Da in jüngster Zeit nur wenige
                    Vergleichstransaktionen für diesen Immobilientyp in dieser
                    Lage stattgefunden haben, ist der ausgewiesene Wert mit
                    einer höheren Unschärfe behaftet.
                  </div>
                )}
              </div>

              {/* Finanzielle Daten Section */}
              <div className="detail-section">
                <h2>Finanzielle Daten</h2>
                <div className="detail-list">
                  <div className="detail-item-row">
                    <div className="detail-item">
                      <label>Kaufpreis</label>
                      <div className="field-value">
                        {formatCurrency(property.kaufpreis)}
                      </div>
                    </div>
                    <div className="detail-item">
                      <label>Kaufdatum</label>
                      <div className="field-value">
                        {property.kaufdatum
                          ? (() => {
                              const parts = property.kaufdatum.split('-');
                              return parts.length === 3
                                ? `${parts[2]}.${parts[1]}.${parts[0]}`
                                : new Date(
                                    property.kaufdatum,
                                  ).toLocaleDateString('de-DE');
                            })()
                          : '-'}
                      </div>
                    </div>
                  </div>

                  <div className="detail-item-row">
                    <div className="detail-item">
                      <label>Verkaufspreis</label>
                      <div className="field-value">
                        {property.sellAmount !== undefined &&
                        property.sellAmount !== null
                          ? formatCurrency(property.sellAmount)
                          : '-'}
                      </div>
                    </div>
                    <div className="detail-item">
                      <label>Verkaufsdatum</label>
                      <div className="field-value">
                        {property.sellDate
                          ? (() => {
                              const parts = property.sellDate.split('-');
                              return parts.length === 3
                                ? `${parts[2]}.${parts[1]}.${parts[0]}`
                                : new Date(
                                    property.sellDate,
                                  ).toLocaleDateString('de-DE');
                            })()
                          : '-'}
                      </div>
                    </div>
                  </div>

                  {property.fremdfinanziert && (
                    <>
                      <div className="detail-item-row">
                        <div className="detail-item">
                          <label>Eigenkapital</label>
                          <div className="field-value">
                            {formatCurrency(property.eigenkapital)}
                          </div>
                        </div>
                        <div className="detail-item">
                          <label>Fremdkapital</label>
                          <div className="field-value">
                            {formatCurrency(property.fremdkapital)}
                          </div>
                        </div>
                      </div>

                      <div className="detail-item-row">
                        <div className="detail-item">
                          <label>Zinssatz</label>
                          <div className="field-value">
                            {formatPercent(property.zinssatz, 2)}
                          </div>
                        </div>
                        <div className="detail-item">
                          <label>Monatliche Rate</label>
                          <div className="field-value">
                            {formatCurrency(property.monatlicheRate)}
                          </div>
                        </div>
                      </div>

                      <div className="detail-item-row">
                        <div className="detail-item">
                          <label>Laufzeit</label>
                          <div className="field-value">
                            {property.laufzeit !== undefined &&
                            property.laufzeit !== null
                              ? `${formatNumber(property.laufzeit)} Jahre`
                              : '-'}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <PropertyFiles propertyId={property.id} />
            </div>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div
          className="confirm-modal-overlay"
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            className="confirm-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Immobilie aus Portfolio entfernen</h3>
            <p>
              Möchten Sie diese Immobilie wirklich aus Ihrem Portfolio
              entfernen? Dieser Vorgang kann nicht rückgängig gemacht werden.
            </p>
            <div className="confirm-modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowDeleteModal(false)}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="btn-confirm"
                onClick={() => {
                  setShowDeleteModal(false);
                  handleDeleteProperty();
                }}
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}

      {showSellWizard && (
        <div
          className="confirm-modal-overlay"
          onClick={() => setShowSellWizard(false)}
        >
          <div
            className="confirm-modal-content"
            style={{ maxWidth: '500px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: '1.5rem' }}>Immobilie verkaufen</h3>
            <div
              style={{
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'column',
                gap: '1.25rem',
                marginBottom: '2rem',
              }}
            >
              <div className="wizard-field">
                <label className="wizard-field-label">Verkaufspreis</label>
                <div className="wizard-field-input-box">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="wizard-input"
                    style={{ paddingRight: '40px' }}
                    placeholder="z.B. 1.300.000"
                    value={
                      sellAmount
                        ? Number(sellAmount).toLocaleString('de-DE')
                        : ''
                    }
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, '');
                      setSellAmount(raw);
                    }}
                  />
                  <span className="wizard-input-suffix">€</span>
                </div>
              </div>

              <div className="wizard-field">
                <label className="wizard-field-label">Verkaufsdatum</label>
                <DatePicker
                  value={sellDate}
                  onChange={(val) => setSellDate(val || '')}
                  placeholder="TT.MM.JJJJ"
                  style={{ paddingRight: '48px' }}
                />
              </div>
            </div>

            <div className="confirm-modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowSellWizard(false)}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="action-button primary"
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                }}
                onClick={handleSellProperty}
                disabled={!sellAmount || !sellDate}
              >
                Verkauf abschließen
              </button>
            </div>
          </div>
        </div>
      )}

      {showBuyModal && (
        <div
          className="confirm-modal-overlay"
          onClick={() => setShowBuyModal(false)}
        >
          <div
            className="confirm-modal-content"
            style={{ maxWidth: '500px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginBottom: '1.5rem' }}>Immobilie kaufen</h3>

            <div
              style={{
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'column',
                gap: '1.25rem',
                marginBottom: '2rem',
              }}
            >
              <div className="wizard-field">
                <label className="wizard-field-label">Kaufpreis</label>
                <div className="wizard-field-input-box">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="wizard-input"
                    style={{ paddingRight: '40px' }}
                    placeholder="z.B. 1.300.000"
                    value={
                      buyPrice
                        ? Number(buyPrice).toLocaleString('de-DE')
                        : ''
                    }
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, '');
                      setBuyPrice(raw);
                    }}
                  />
                  <span className="wizard-input-suffix">€</span>
                </div>
              </div>

              <div className="wizard-field">
                <label className="wizard-field-label">Kaufdatum</label>
                <DatePicker
                  value={buyDate}
                  onChange={(val) => setBuyDate(val || '')}
                  placeholder="TT.MM.JJJJ"
                  style={{ paddingRight: '48px' }}
                />
              </div>
            </div>

            <div className="confirm-modal-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setShowBuyModal(false)}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="action-button primary"
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                }}
                onClick={handleBuyProperty}
                disabled={!buyPrice || !buyDate}
              >
                Kauf abschließen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyPage;
